import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import {
  MapPin, MagnifyingGlass, Lightning, Plus, Minus, User,
  Storefront, ShoppingCart, ArrowRight, NavigationArrow,
  CaretRight
} from "@phosphor-icons/react";

export default function CustomerHome() {
  const { user, logout, hasRole, switchRole } = useAuth();
  const nav = useNavigate();
  const { pincode, setPincode, add, decrement, getStoreCart, activeCart, setSelectedAddress } = useCart();
  const [showPincodeModal, setShowPincodeModal] = useState(!pincode);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Auto-prefer default saved address pincode for logged-in users
  useEffect(() => {
    if (!user || pincode) return;
    api.get("/customer/addresses").then((r) => {
      const def = (r.data || []).find((a) => a.is_default) || (r.data || [])[0];
      if (def) {
        setSelectedAddress(def);
        setShowPincodeModal(false);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const loadAll = async (pin) => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        api.get("/customer/stores", { params: { pincode: pin } }),
        api.get("/customer/categories", { params: { pincode: pin } }),
        api.get("/customer/products", { params: { pincode: pin } }),
      ]);
      setStores(s.data);
      setCategories(c.data);
      setProducts(p.data);
    } catch {
      toast.error("Failed to load marketplace");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (pincode && !showPincodeModal) loadAll(pincode);
    // eslint-disable-next-line
  }, [pincode]);

  useEffect(() => {
    if (!pincode || showPincodeModal) return;
    const params = { pincode };
    if (activeCat) params.category_name = activeCat;
    if (query) params.q = query;
    api.get("/customer/products", { params }).then((r) => setProducts(r.data));
  }, [activeCat, query, pincode, showPincodeModal]);

  return (
    <div className="min-h-screen bg-[#F9F9F9] pb-32">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-gray-100">
        <div className="px-4 py-3 max-w-5xl mx-auto flex items-center gap-3">
          <Link to="/home" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-9 h-9 rounded-xl bg-[#15803D] flex items-center justify-center">
              <Lightning weight="fill" size={18} className="text-white" />
            </div>
            <span className="font-heading font-black text-lg hidden sm:inline">QuickDukaan</span>
          </Link>

          <button onClick={() => setShowPincodeModal(true)}
            data-testid="home-change-pincode"
            className="flex-1 min-w-0 flex items-center gap-2 bg-[#F8CB46]/15 hover:bg-[#F8CB46]/25 rounded-xl px-3 py-2 transition">
            <MapPin weight="fill" size={16} className="text-[#15803D] flex-shrink-0" />
            <div className="min-w-0 flex-1 text-left">
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-600 leading-tight">Deliver to</div>
              <div className="text-sm font-bold truncate">{pincode || "Select pincode"}</div>
            </div>
            <CaretRight size={14} className="text-gray-500" />
          </button>

          {user ? (
            <div className="relative group" data-testid="home-account-menu">
              <button className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-testid="home-account">
                <User size={18} />
              </button>
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition z-50">
                <Link to="/my-orders" className="block px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 rounded-t-2xl" data-testid="home-menu-orders">My orders</Link>
                <Link to="/account/addresses" className="block px-4 py-2.5 text-sm font-semibold hover:bg-gray-50" data-testid="home-menu-addresses">My addresses</Link>
                {hasRole("shopkeeper") && (
                  <button onClick={async () => { try { await switchRole("shopkeeper"); nav(user?.store_id ? "/admin" : "/onboard"); } catch { toast.error("Failed to switch"); } }}
                    className="w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 text-[#15803D] border-t border-gray-100"
                    data-testid="home-menu-seller-dashboard">
                    Go to Seller Dashboard →
                  </button>
                )}
                <button onClick={() => { logout(); nav("/"); }} className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-b-2xl border-t border-gray-100" data-testid="home-menu-logout">Logout</button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="text-sm font-semibold px-3 py-2 bg-gray-100 rounded-full" data-testid="home-login-btn">Login</Link>
          )}
        </div>

        {/* Search */}
        <div className="px-4 pb-3 max-w-5xl mx-auto">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="qd-input pl-10 !bg-white"
              placeholder="Search across all stores in your area…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="home-search"
            />
          </div>
        </div>
      </header>

      {showPincodeModal && (
        <PincodeModal
          initial={pincode}
          onClose={() => {
            if (pincode) {
              setShowPincodeModal(false);
              localStorage.setItem("qd_pincode_chosen", "1");
            }
          }}
          onSave={(pin) => {
            setPincode(pin);
            localStorage.setItem("qd_pincode_chosen", "1");
            setShowPincodeModal(false);
          }}
        />
      )}

      <main className="max-w-5xl mx-auto px-4 pt-4 space-y-6">
        {/* Stores near you */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="font-heading font-black text-xl">Stores near you</h2>
              <p className="text-xs text-gray-500">{stores.length} delivering to {pincode || "—"}</p>
            </div>
          </div>

          {loading ? (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-32 w-72 rounded-2xl flex-shrink-0" />)}
            </div>
          ) : stores.length === 0 ? (
            <EmptyCard
              icon={Storefront}
              title="No stores in this pincode yet"
              desc="We're expanding! Try changing your pincode."
              cta={{ label: "Change pincode", onClick: () => setShowPincodeModal(true), testId: "empty-stores-change-pin" }}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stores.map((s) => {
                const closed = s.availability_status && !s.availability_status.is_open;
                return (
                <Link key={s.id} to={`/${s.slug}`}
                  className={`qd-card overflow-hidden flex group relative ${closed ? "opacity-75" : ""}`}
                  data-testid={`home-store-${s.slug}`}>
                  <div className="w-24 h-auto bg-gray-50 flex-shrink-0 overflow-hidden">
                    {s.logo
                      ? <img src={s.logo} alt={s.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-[#F8CB46]/30 flex items-center justify-center"><Storefront weight="fill" size={28} className="text-[#15803D]" /></div>}
                  </div>
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-heading font-black text-base truncate">{s.name}</div>
                      {closed && (
                        <span className="text-[9px] uppercase tracking-widest font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full"
                          data-testid={`home-store-closed-${s.slug}`}>Closed</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-1">{s.description}</div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-600">
                      {closed ? (
                        <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-bold">
                          Opens at {s.availability_status?.open_time || "09:00"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-[#F8CB46]/20 px-2 py-0.5 rounded-full font-bold">
                          <Lightning size={10} weight="fill" className="text-[#15803D]" /> {s.eta_minutes} min
                        </span>
                      )}
                      <span>{s.product_count} items</span>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#15803D]">
                      {closed ? "Browse only" : "View store"} <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition" />
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Categories */}
        {categories.length > 0 && (
          <section>
            <h2 className="font-heading font-black text-xl mb-2">Shop by category</h2>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              <button onClick={() => setActiveCat(null)}
                data-testid="home-cat-all"
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${!activeCat ? "bg-black text-white" : "bg-white border border-gray-200"}`}>
                All
              </button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => setActiveCat(c.name === activeCat ? null : c.name)}
                  data-testid={`home-cat-${c.name}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 ${activeCat === c.name ? "bg-black text-white" : "bg-white border border-gray-200"}`}>
                  {c.icon && <img src={c.icon} alt="" className="w-4 h-4" />}
                  {c.name}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Products grid */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading font-black text-xl">
              {query ? `Results for "${query}"` : activeCat ? activeCat : "Popular near you"}
            </h2>
            <span className="text-xs text-gray-500">{products.length} items</span>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <div key={i} className="skeleton aspect-[3/4] rounded-2xl" />)}
            </div>
          ) : products.length === 0 ? (
            <EmptyCard
              icon={ShoppingCart}
              title="No live products available for this pincode"
              desc="Check back soon or try a different pincode."
              cta={{ label: "Change pincode", onClick: () => setShowPincodeModal(true), testId: "empty-prod-change-pin" }}
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} add={add} decrement={decrement} getStoreCart={getStoreCart} />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer bar - shown only when cart is empty */}
      {!activeCart && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100 p-3 flex items-center justify-between max-w-5xl mx-auto">
          <Link to="/my-orders" className="text-xs font-bold text-gray-600 hover:text-black" data-testid="home-my-orders">My orders</Link>
          {user ? (
            <button onClick={() => { logout(); nav("/"); }} className="text-xs font-bold text-red-500" data-testid="home-logout">Logout</button>
          ) : (
            <Link to="/login" className="text-xs font-bold text-[#15803D]">Login</Link>
          )}
        </div>
      )}

      {/* Sticky cart bar - shown when items are in cart */}
      {activeCart && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 fade-up">
          <button onClick={() => nav(`/${activeCart.slug}/cart`)}
            className="w-full max-w-5xl mx-auto bg-[#15803D] text-white rounded-2xl p-4 flex items-center justify-between shadow-2xl hover:bg-[#166534] active:scale-[0.98] transition"
            data-testid="home-sticky-cart">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center relative">
                <ShoppingCart weight="fill" size={20} />
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#F8CB46] text-black text-[10px] font-black flex items-center justify-center">
                  {activeCart.count}
                </span>
              </div>
              <div className="text-left">
                <div className="font-heading font-black">{activeCart.count} item{activeCart.count > 1 && "s"}</div>
                <div className="text-xs text-white/80">₹{activeCart.total} · View cart</div>
              </div>
            </div>
            <div className="font-heading font-black">Checkout →</div>
          </button>
        </div>
      )}
    </div>
  );
}

function ProductCard({ p, add, decrement, getStoreCart }) {
  const cart = getStoreCart(p.store_slug);
  const inCart = cart.items.find((i) => i.product_id === p.id);
  return (
    <div className="qd-card overflow-hidden" data-testid={`home-product-${p.id}`}>
      <Link to={`/${p.store_slug}/product/${p.id}`}>
        <div className="aspect-square bg-gray-50 overflow-hidden relative">
          {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
          {p.discount_pct > 0 && (
            <div className="absolute top-2 left-2 bg-[#15803D] text-white text-[10px] font-black px-2 py-0.5 rounded-full">
              {p.discount_pct}% OFF
            </div>
          )}
        </div>
      </Link>
      <div className="p-2.5">
        <div className="text-[10px] text-gray-500 truncate">{p.store_name}</div>
        <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem]">{p.name}</div>
        <div className="mt-1 flex items-center justify-between">
          <div>
            <div className="font-heading font-black">₹{p.price}</div>
            {p.mrp > p.price && <div className="text-[10px] text-gray-400 line-through">₹{p.mrp}</div>}
          </div>
          {inCart ? (
            <div className="flex items-center gap-1 bg-[#15803D] text-white rounded-lg overflow-hidden">
              <button onClick={() => decrement(p.store_slug, p.id)} className="px-2 py-1.5" data-testid={`home-dec-${p.id}`}><Minus size={12} weight="bold" /></button>
              <span className="text-xs font-bold min-w-[20px] text-center">{inCart.qty}</span>
              <button onClick={() => add(p.store_slug, p.store_id, p)} className="px-2 py-1.5" data-testid={`home-inc-${p.id}`}><Plus size={12} weight="bold" /></button>
            </div>
          ) : (
            <button onClick={() => add(p.store_slug, p.store_id, p)}
              className="border-2 border-[#15803D] text-[#15803D] font-bold text-xs px-3 py-1 rounded-lg hover:bg-[#15803D] hover:text-white transition active:scale-95"
              data-testid={`home-add-${p.id}`}>
              ADD
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyCard({ icon: Icon, title, desc, cta }) {
  return (
    <div className="qd-card p-8 text-center">
      <Icon weight="duotone" size={40} className="text-gray-400 mx-auto" />
      <div className="font-heading font-bold text-base mt-3">{title}</div>
      <div className="text-sm text-gray-500 mt-1">{desc}</div>
      {cta && (
        <button onClick={cta.onClick} className="qd-btn-primary mt-4 text-sm" data-testid={cta.testId}>
          {cta.label}
        </button>
      )}
    </div>
  );
}

function PincodeModal({ initial, onClose, onSave }) {
  const [pin, setPin] = useState(initial || "");
  const [detecting, setDetecting] = useState(false);

  const detectLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported in this browser");
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.get("/customer/reverse-geocode", {
            params: { lat: latitude, lon: longitude },
          });
          if (r.data.pincode) {
            setPin(r.data.pincode);
            const where = r.data.locality || r.data.city || "your area";
            toast.success(`Detected: ${where} · ${r.data.pincode}`);
          } else {
            toast.error("Couldn't detect a pincode here. Please enter manually.");
          }
        } catch {
          toast.error("Couldn't detect pincode. Please enter manually.");
        }
        setDetecting(false);
      },
      (err) => {
        if (err.code === 1) toast.error("Location permission denied. Enter pincode manually.");
        else toast.error("Couldn't get location. Enter pincode manually.");
        setDetecting(false);
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  };

  const save = (e) => {
    e?.preventDefault();
    if (pin.length !== 6) return toast.error("Enter a valid 6-digit pincode");
    onSave(pin);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up">
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl p-6"
        data-testid="pincode-modal">
        <div className="text-center mb-5">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#F8CB46]/20 items-center justify-center mb-3">
            <MapPin weight="fill" size={28} className="text-[#15803D]" />
          </div>
          <h2 className="font-heading font-black text-2xl">Where do we deliver?</h2>
          <p className="text-sm text-gray-600 mt-1">Tell us your pincode to see stores nearby</p>
        </div>

        <button onClick={detectLocation} disabled={detecting}
          data-testid="pincode-detect-btn"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#15803D] hover:bg-[#15803D]/5 transition font-semibold text-sm">
          <NavigationArrow weight={detecting ? "fill" : "bold"} size={18} className="text-[#15803D]" />
          {detecting ? "Detecting…" : "Detect my location"}
        </button>

        <div className="text-center text-xs text-gray-400 my-4 uppercase tracking-widest font-bold">or</div>

        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Pincode</label>
            <input
              data-testid="pincode-modal-input"
              type="tel" inputMode="numeric" maxLength={6} autoFocus
              className="qd-input mt-2 font-mono text-2xl tracking-[0.3em] text-center"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="110034"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["110034", "110085", "110052", "110086", "110088"].map((p) => (
              <button type="button" key={p} onClick={() => setPin(p)}
                className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 font-mono"
                data-testid={`pincode-suggest-${p}`}>
                {p}
              </button>
            ))}
          </div>
          <button type="submit" className="qd-btn-green w-full" data-testid="pincode-modal-save">
            Continue
          </button>
          {initial && (
            <button type="button" onClick={onClose} className="text-xs text-gray-500 w-full text-center underline">
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
